import { useEffect, useMemo, useState } from "react";
import { Database, Leaf, RefreshCw, Sparkles } from "lucide-react";
import { api, ApiError } from "../../api";
import { useAuth } from "../../auth";
import { Button, Card, Field, cx } from "../../ui";

type ChatMetrics = {
  windowDays: number;
  summary: {
    totalAssistantTurns: number;
    fallbackCount: number;
    fallbackRate: number;
  };
  pathBreakdown: Array<{ path: string; count: number }>;
  topFallbackPrompts: Array<{
    prompt: string;
    count: number;
    lastSeenAt: string;
  }>;
};

const WINDOWS = [7, 14, 30, 90] as const;

/**
 * Site-admin chat metrics. Surfaces the fallback rate (% of Elm
 * replies that hit the generic catch-all) and the most-frequent
 * recent prompts that triggered it. The point of the page is the
 * feedback loop: an admin sees the same question keep falling
 * through, then writes a deterministic INTENT or expands a
 * keyword list to handle it.
 *
 * No write actions live here — promoting a prompt into a new
 * intent is a code change, not a runtime config. The page is
 * intentionally read-only triage.
 */
export function AdminElmMetrics() {
  const [days, setDays] = useState<number>(14);
  const [data, setData] = useState<ChatMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<ChatMetrics>(`/api/admin/chat-metrics?days=${days}`)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? err.status === 403
              ? "Site-admin access only."
              : `HTTP ${err.status}`
            : String(err),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const fallbackPct = useMemo(() => {
    if (!data) return null;
    return (data.summary.fallbackRate * 100).toFixed(1);
  }, [data]);

  const totalForBreakdown = useMemo(() => {
    if (!data) return 0;
    return data.pathBreakdown.reduce((s, r) => s + r.count, 0);
  }, [data]);

  return (
    <div>
      <Card>
        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
          <div>
            <h2 className="text-ink-800 m-0 inline-flex items-center gap-2">
              <Leaf size={18} className="text-brand-700" />
              Elm chat metrics
            </h2>
            <p className="text-[12.5px] text-ink-500 mt-1 mb-0 max-w-prose">
              Triage which questions Elm couldn't answer. Promote
              high-frequency prompts into a deterministic intent or
              keyword in <code>src/services/elmChat.ts</code>.
            </p>
          </div>
          <Field label="Window" hint="Last N days of chat history.">
            <select
              className="select"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {WINDOWS.map((w) => (
                <option key={w} value={w}>
                  {w} days
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      {error && (
        <Card>
          <div className="alert-error">{error}</div>
        </Card>
      )}

      {loading && !data ? (
        <Card>
          <p className="text-ink-500 text-sm">Loading…</p>
        </Card>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <SummaryTile
              label="Assistant turns"
              value={data.summary.totalAssistantTurns.toLocaleString()}
              hint={`Last ${data.windowDays} days`}
            />
            <SummaryTile
              label="Fallback count"
              value={data.summary.fallbackCount.toLocaleString()}
              hint="Replies Elm couldn't answer"
              tone={data.summary.fallbackCount > 0 ? "warn" : "ok"}
            />
            <SummaryTile
              label="Fallback rate"
              value={fallbackPct !== null ? `${fallbackPct}%` : "—"}
              hint="Lower is better"
              tone={
                data.summary.fallbackRate >= 0.1
                  ? "warn"
                  : data.summary.fallbackRate >= 0.03
                    ? "neutral"
                    : "ok"
              }
            />
          </div>

          <Card>
            <h3 className="text-ink-800 m-0 mb-2">Reply path breakdown</h3>
            <p className="text-[11.5px] text-ink-500 mb-3">
              Where Elm's replies came from. Heavy traffic on{" "}
              <code>generic_fallback</code> means new INTENTS are needed;
              heavy <code>llm</code> traffic means the rule-based router
              is missing common intents.
            </p>
            {data.pathBreakdown.length === 0 ? (
              <p className="text-ink-500 text-sm">
                No assistant turns recorded in this window.
              </p>
            ) : (
              <ul className="flex flex-col gap-1.5 m-0 p-0 list-none">
                {data.pathBreakdown.map((row) => {
                  const pct =
                    totalForBreakdown === 0
                      ? 0
                      : (row.count / totalForBreakdown) * 100;
                  return (
                    <li
                      key={row.path}
                      className="flex items-center gap-3 text-[13px]"
                    >
                      <code
                        className={cx(
                          "min-w-[160px] px-1.5 py-0.5 rounded text-[11.5px]",
                          row.path === "generic_fallback"
                            ? "bg-status-rejected-bg text-status-rejected-fg"
                            : row.path === "llm"
                              ? "bg-brand-50 text-brand-800"
                              : row.path.startsWith("intent:")
                                ? "bg-status-approved-bg text-status-approved-fg"
                                : "bg-sand-100 text-ink-700",
                        )}
                      >
                        {row.path}
                      </code>
                      <div className="flex-1 h-1.5 bg-sand-100 rounded-full overflow-hidden">
                        <div
                          className={cx(
                            "h-full",
                            row.path === "generic_fallback"
                              ? "bg-status-rejected-fg"
                              : "bg-brand-700",
                          )}
                          style={{ width: `${Math.max(2, pct)}%` }}
                        />
                      </div>
                      <span className="text-ink-700 font-medium tabular-nums w-14 text-right">
                        {row.count.toLocaleString()}
                      </span>
                      <span className="text-ink-500 text-[11.5px] tabular-nums w-12 text-right">
                        {pct.toFixed(1)}%
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card>
            <h3 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
              <Sparkles size={15} className="text-brand-700" />
              Top unanswered prompts
            </h3>
            <p className="text-[11.5px] text-ink-500 mb-3">
              The most-frequent user messages whose immediate next
              reply was the generic catch-all. If the same question
              shows up two or three times, write an INTENT for it.
            </p>
            {data.topFallbackPrompts.length === 0 ? (
              <p className="text-ink-500 text-sm">
                No fallback prompts in this window. Elm answered everything.
              </p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Prompt</th>
                      <th className="text-right">Count</th>
                      <th>Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topFallbackPrompts.map((row, i) => (
                      <tr key={i}>
                        <td className="text-[13px] text-ink-800">
                          <span className="whitespace-pre-wrap break-words">
                            {row.prompt}
                          </span>
                        </td>
                        <td className="text-right tabular-nums font-medium">
                          {row.count}
                        </td>
                        <td className="text-xs text-ink-500">
                          {row.lastSeenAt
                            ? new Date(row.lastSeenAt).toLocaleString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <h3 className="text-ink-800 m-0 mb-2">
              How to act on this
            </h3>
            <ol className="text-[13px] text-ink-700 leading-relaxed pl-5">
              <li>
                If a prompt repeats, open{" "}
                <code>src/services/elmChat.ts</code> and add an entry
                to <code>INTENTS</code> with patterns + a reply.
              </li>
              <li>
                If a phrasing is close to an existing intent, expand
                that intent's <code>patterns</code> regex instead of
                writing a new one.
              </li>
              <li>
                If the prompt is a scope-of-work ask for a project
                type that didn't match, extend{" "}
                <code>keywordsFor()</code> with the new alias.
              </li>
              <li>
                Re-deploy. Future occurrences will land in the
                appropriate <code>intent:&lt;id&gt;</code> bucket
                instead of <code>generic_fallback</code>.
              </li>
            </ol>
          </Card>

          <CorpusReindexCard />

          <div className="text-right">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setData(null);
                setLoading(true);
                api
                  .get<ChatMetrics>(`/api/admin/chat-metrics?days=${days}`)
                  .then(setData)
                  .finally(() => setLoading(false));
              }}
            >
              Refresh
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function SummaryTile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "ok" | "neutral" | "warn";
}) {
  const accent =
    tone === "warn"
      ? "text-status-rejected-fg"
      : tone === "ok"
        ? "text-status-approved-fg"
        : "text-ink-800";
  return (
    <Card className="!p-4">
      <div className="text-[10.5px] uppercase tracking-wider text-ink-500 mb-1">
        {label}
      </div>
      <div className={cx("text-[22px] font-semibold leading-none", accent)}>
        {value}
      </div>
      {hint && (
        <div className="text-[11.5px] text-ink-500 mt-1.5">{hint}</div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Corpus reindex — admin-triggered run of the Elm RAG indexer for a
// specific building. The endpoint (POST /api/admin/elm-corpus/reindex)
// is rate-limited to once per hour per building; we surface the
// 429 retry window here instead of letting the user mash the button
// and watch errors pile up.
//
// The site admin's role grants double as the building selector — we
// only let them reindex buildings they have a building-scoped admin
// or PM role on, which mirrors `principalIsBuildingAdmin` server-side.
// ---------------------------------------------------------------------------

type ReindexResult = {
  ok: true;
  buildingId: string;
  embedded: number;
  skipped: number;
  removed: number;
  bySource: Partial<Record<string, number>>;
};

type ReindexError =
  | { kind: "embeddings_not_configured" }
  | { kind: "rate_limited"; retryAfterSec: number }
  | { kind: "forbidden" }
  | { kind: "other"; message: string };

function CorpusReindexCard() {
  const { state } = useAuth();
  const buildings = useMemo(() => {
    if (state.status !== "authenticated") return [];
    const seen = new Map<string, string>();
    for (const r of state.me.roles) {
      if (r.code !== "admin" && r.code !== "property_manager") continue;
      if (!seen.has(r.buildingId)) {
        seen.set(r.buildingId, r.buildingName ?? "(unnamed building)");
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [state]);

  const [buildingId, setBuildingId] = useState<string>(
    () => buildings[0]?.id ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ReindexResult | null>(null);
  const [error, setError] = useState<ReindexError | null>(null);

  // The endpoint enforces a one-hour cooldown per building. When it
  // 429s we capture `retryAfterSec` and tick it down so the operator
  // can see when the next attempt is allowed instead of mashing the
  // button and watching errors pile up.
  const [cooldownSec, setCooldownSec] = useState(0);
  useEffect(() => {
    if (cooldownSec <= 0) return;
    const t = window.setTimeout(() => setCooldownSec((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldownSec]);

  // Initialize the dropdown selection once the auth state resolves.
  useEffect(() => {
    if (!buildingId && buildings.length > 0) setBuildingId(buildings[0]!.id);
  }, [buildings, buildingId]);

  async function handleReindex() {
    if (!buildingId) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.post<ReindexResult>(
        "/api/admin/elm-corpus/reindex",
        { buildingId },
      );
      setResult(res);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = (err.body ?? {}) as {
          error?: string;
          retryAfterSec?: number;
        };
        if (err.status === 429) {
          const retry = body.retryAfterSec ?? 3600;
          setCooldownSec(retry);
          setError({ kind: "rate_limited", retryAfterSec: retry });
        } else if (err.status === 503) {
          setError({ kind: "embeddings_not_configured" });
        } else if (err.status === 403) {
          setError({ kind: "forbidden" });
        } else {
          setError({ kind: "other", message: `HTTP ${err.status}` });
        }
      } else {
        setError({ kind: "other", message: String(err) });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h3 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
        <Database size={15} className="text-brand-700" />
        Corpus index
      </h3>
      <p className="text-[12.5px] text-ink-500 mb-3 max-w-prose">
        Rebuild the per-building retrieval index used by the Elm RAG
        fallback (see <code>docs/elm-rag-spec.md</code>). The indexer
        is idempotent — chunks unchanged since the last run are
        detected by content hash and skipped without an embedding API
        call. The scheduler also runs this nightly; this button is
        for after a rules edit when you don't want to wait.
      </p>

      {buildings.length === 0 ? (
        <p className="text-ink-500 text-sm">
          You don't have a building-scoped admin or PM role, so there's
          nothing to reindex from this account.
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <Field
            label="Building"
            hint="Reindex affects this building only."
          >
            <select
              className="select"
              value={buildingId}
              onChange={(e) => {
                setBuildingId(e.target.value);
                setResult(null);
                setError(null);
              }}
              disabled={busy}
            >
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
          <Button
            type="button"
            variant="primary"
            onClick={handleReindex}
            loading={busy}
            disabled={busy || cooldownSec > 0 || !buildingId}
          >
            <RefreshCw size={14} />
            {busy ? "Reindexing…" : "Reindex now"}
          </Button>
          {cooldownSec > 0 && (
            <span className="text-[12px] text-ink-500">
              Rate-limited — try again in {formatCooldown(cooldownSec)}.
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="alert-error mt-3 text-[13px]">
          {error.kind === "embeddings_not_configured"
            ? "VOYAGE_API_KEY is not set on the server. Configure it and restart the API to enable retrieval."
            : error.kind === "rate_limited"
              ? `Rate-limited — try again in ${formatCooldown(error.retryAfterSec)}.`
              : error.kind === "forbidden"
                ? "You don't have permission to reindex this building."
                : error.message}
        </div>
      )}

      {result && (
        <div className="mt-3 text-[13px] text-ink-700">
          <div className="font-medium mb-1">Reindex complete.</div>
          <ul className="list-none p-0 m-0 flex flex-wrap gap-x-5 gap-y-1">
            <ResultStat label="Embedded" value={result.embedded} />
            <ResultStat label="Skipped (unchanged)" value={result.skipped} />
            <ResultStat label="Removed" value={result.removed} />
          </ul>
          {Object.keys(result.bySource).length > 0 && (
            <div className="text-[11.5px] text-ink-500 mt-2">
              By source:{" "}
              {Object.entries(result.bySource)
                .map(([k, v]) => `${k}=${v}`)
                .join(" · ")}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function ResultStat({ label, value }: { label: string; value: number }) {
  return (
    <li className="inline-flex items-baseline gap-1.5">
      <span className="text-ink-500 text-[11.5px] uppercase tracking-wider">
        {label}
      </span>
      <span className="font-semibold tabular-nums">{value}</span>
    </li>
  );
}

function formatCooldown(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}
